'use strict';
/* ==========================================================================
   levels/jingle.js — step "jingle" (any): COINS DON'T REMEMBER, the retro TV game-show jingle as a notebook lyric video
   (DESIGN.md §5.4). Model: lessons/through-zero/levels/jingle.js (the same pack, the same structure).

   ► The song: assets/music/jingle.m4a (ACE-Step take, assets agent) + assets/music/jingle_words.json
     (tools/song_timing.py): the TRUE lyric lines of content/jingle.json with word times, and the beat grid {bpm,
     offset}. NOTHING here hard-codes a time: acts are found by the lines' SECTIONS (Intro, Verse 1, Chorus, Verse 2,
     Bridge, Chorus, Outro) and every picture beat is keyed to a sung word (S.W(line, word)) or to the beat grid, so a
     new take with the same lyric sheet needs no code change.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact.
   EVERY NUMBER on screen comes from core-local/chance.js (see nums()): the coin's flips are Chance.simulate(coin, 1000,
   SEED) (seed 21 opens with a run of five heads: the verse's streak IS the simulation's, counted, not typed), the
   die's rolls Chance.simulate(die, …, SEED), P(tails) / "1 out of 2" from Chance.count / Chance.P of a coin, the jar
   from Chance.VIDEO.gumballs (P(red) = 3/4, its likelihood word), the bridge's 8 and 800 spins from Chance.VIDEO
   (s4Seed / s4Big: the video's own runs, 2 of 8 and s4Big.socks of 800), the dashed theory lines from Chance.P.

   Acts (by section):
     intro    the game-show marquee (bulbs blinking on the beat), the title, a coin spinning in the air on the beat,
              "THE GAME OF CHANCE"
     verse1   the coin flips on the beat and lands H five times (the simulation's first five flips): FLIPS H H H H H ?;
              Jay's "TAILS IS DUE?" sticky, struck on "can't"; the coin's "I have no memory."; H and T coins,
              "HEADS OR TAILS: 1 OUT OF 2", P(T) = 1/2; "1/2" under every flip, the red one under the "?"
     chorus1  the hook line big; the coin flips on every beat (its face = the next simulated flip) and the tally chart
              (share of heads after each flip, H / T under the axis) wobbles from 1 toward the dashed 1/2; the die
              rolls on the beat from "Dice"
     verse2   the gumball jar (3 red + 1 blue), counted 1–4; "2 COLORS = 50/50?" struck; P(red) = 3/4 = 0.75 = 75%;
              P = favorable ÷ total; the 0–1 likelihood line (IMPOSSIBLE … CERTAIN), P(red) lands on LIKELY
     bridge   the prize wheel's 8 spins (the share wobbles: 2 of 8 = 1/4) beside the 800 spins (it settles near the
              dashed 1/8)
     chorus2  the hook big; the coin and die on the beat; the chart runs to 1000 flips and settles near 1/2
     outro    "Coins don't remember!", "Nothing's ever due!", the rules card; the LUCKY stamp on "due!"
   Karaoke (the index card at the bottom): the current line written on word by word in Kalam, the sung word under the
   highlighter, the hook words in red pen, the next line faint below.
   Jay (left) and Ms. Rivera (right) are small line-art figures bobbing on the beat; they point on the key words.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom.
   "Finish" appears once ≥ 90% of the song has been heard; the end of a listen with ≥ 90% heard stamps the step.
   Portrait phone: the stage on top, Dock.karaoke + ↺ ▶ ⌂ + the strip below.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared
   <audio> element (Audio.media), never decoded into memory.

   QA hooks: sc.song (S(i), E(i), W(i, word), beats, sec), sc.plan.acts, sc.nums, sc.debugAt(t) (freeze at song time
   t), sc.now(), sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done(), sc.finish(),
   sc.lyricAt(t), sc.activeWord(t), sc.actAt(t), sc.heard, sc.ended, sc.audioReady, sc.canFinish();
   Levels.jingle.nums(), Levels.jingle.flipsAt(song, plan, t) (the chorus charts' landed flips at song time t).
   ========================================================================== */
(function () {
  const STEP = 'jingle';
  const AUDIO = 'music/jingle.m4a', WORDS = 'music/jingle_words.json';
  const KEY = 'jingle:tails';
  const PN = NB.PAL, FONT = NB.FONT;
  const BAND_Y = 846, CUR_Y = 934, NEXT_Y = 1012, CUR_SIZE = 62, NEXT_SIZE = 38;
  const TURN = 0.45;                                   // the page turn between acts (s)
  const FINISH_AT = 0.9;                               // share of the song heard before "Finish" / the stamp
  const CREDIT = 'AI-generated jingle · the maths is real';
  const TITLE = "Coins Don't Remember";
  const STAMP = 'LUCKY';

  /* ---------------- the numbers (chance.js only) ---------------- */
  const SEED = 21, FLIPS = 1000, ROLLS = 64;           // seed 21: the simulated coin opens with five heads
  const ft = v => Chance.text(v);
  let NUMS = null;
  function nums() {
    if (NUMS) return NUMS;
    const V = Chance.VIDEO;
    // the coin: 1000 seeded flips; the streak the verse sings = the simulation's opening run of heads
    const coin = Chance.coin(), flips = Chance.simulate(coin, FLIPS, SEED, 'is:H');
    let streak = 0; while (streak < FLIPS && flips.outcomes[streak] === 'H') streak++;
    const c = { coin, n: coin.n, tails: Chance.count(coin, 'is:T'), pT: Chance.P(coin, 'is:T'), pH: Chance.P(coin, 'is:H'), flips, streak };
    // the die: seeded rolls (faces only; the chorus's "dice don't keep score")
    const die = Chance.die(), rolls = Chance.simulate(die, ROLLS, SEED);
    const d = { die, faces: rolls.idx.map(k => die.outcomes[k].value) };
    // the jar: 3 red + 1 blue; P(red) and its likelihood word
    const jar = Chance.exp(V.gumballs), grp = l => jar.groups.find(x => x.label === l).count;
    const j = { jar, balls: jar.outcomes.map(o => o.key), red: grp('RED'), blue: grp('BLUE'), n: jar.n, colors: jar.groups.length,
      pRed: Chance.P(jar, 'is:RED') };
    j.word = Chance.likelihood(j.pRed); j.decpct = Chance.decpct(j.pRed);
    // the wheel: the video's 8 spins and 800 spins (the same seed: the 800 start with the 8)
    const wheel = Chance.exp(V.wheel), SOCKS = 'label:FREE SOCKS';
    const small = Chance.simulate(wheel, 8, V.s4Seed, SOCKS), big = Chance.simulate(wheel, V.s4Big.n, V.s4Big.seed, SOCKS);
    const w = { wheel, p: Chance.P(wheel, SOCKS), small, big, smallShare: Chance.div(small.x, small.n), bigShare: Chance.div(big.x, big.n) };
    NUMS = { coin: c, die: d, jar: j, wheel: w };
    return NUMS;
  }

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 80;
      const b = d.beats || {};
      this.bpm = b.bpm || 124; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
      // the sections as runs of consecutive lines: [{name, lines: [i…]}]
      this.sec = [];
      d.lines.forEach((l, i) => { const last = this.sec[this.sec.length - 1]; if (last && last.name === l.section) last.lines.push(i); else this.sec.push({ name: l.section, lines: [i] }); });
    }
    /* the n-th run of a section (Chorus 1, Chorus 2) → its line indices ([] if absent) */
    section(name, n = 1) { const s = this.sec.filter(x => x.name === name)[n - 1]; return s ? s.lines : []; }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) { if (window.__songCueMiss) window.__songCueMiss.push('?' + i + ':' + word); return 1e6; }
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    /* the first match of `word` in any of the lines */
    WA(lines, word) {
      const k = normW(word);
      for (const i of lines) for (const w of this.lines[i].words) if (normW(w.w) === k) return w.s;
      if (window.__songCueMiss) window.__songCueMiss.push(lines.join('/') + ':' + word);
      return lines.length ? this.S(lines[0]) : 1e6;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off - 0.01 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
    /* the first beat index at/after t0 (a cue a hair early still counts as on that beat) */
    beatAfter(t0) { return Math.ceil(this.beatPos(t0 - 0.12)); }
    /* 0 → 1 over n beats starting on the first beat at/after t0, each beat a quick stroke (pure in t) */
    onBeats(t, t0, n) {
      const tb = this.off + this.beatAfter(t0) * this.bt;
      if (t < tb) return 0;
      const pos = (t - tb) / this.bt, k = Math.floor(pos);
      if (k >= n) return 1;
      return (k + E.outCubic(Math.min(1, (pos - k) / 0.45))) / n;
    }
  }

  /* ---------------- small drawing helpers ---------------- */
  const SL = new Map();
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  function art(key, bx, by, bw, bh, draw) {
    let sl = SL.get(key); if (!sl) SL.set(key, sl = {});
    if (FX.sprites && fontOk() && !_spriteBaking && _ctm() && spriteIn(sl, bx, by, bw, bh, draw)) return;
    draw();
  }
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  const hand = (size, w = 700) => `${w} ${size}px ${FONT.hand}`;
  const uif = (size, w = 700) => `${w} ${size}px ${FONT.ui}`;
  function rrect(x, y, w, h, r) { g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r); g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath(); }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  function multiply(fn) { g.save(); if (!(window.Quality && Quality.tier === 'low')) g.globalCompositeOperation = 'multiply'; try { fn(); } finally { g.restore(); } }
  const say = (text, x, y, t0, o = {}) => NB.write(text, x, y, { size: o.size || 56, weight: o.weight || 700, align: o.align || 'center', t0, dur: o.dur, color: o.color, font: o.font });
  /* a label on a small paper backing (x = left, y = middle) so it reads over grid lines */
  function plabel(text, x, y, t0, o = {}) {
    if (T < t0) return;
    const size = o.size || 32, f = o.font === 'ui' ? uif(size) : hand(size), w = tw(text, f), col = o.color || PN.ink;
    const x0 = o.align === 'right' ? x - w : o.align === 'center' ? x - w / 2 : x;
    alpha(P(T, t0, t0 + 0.2), () => { g.fillStyle = PN.page; rrect(x0 - 8, y - size * 0.62, w + 16, size * 1.24, 8); g.fill(); });
    NB.write(text, x0, y, { size, weight: 700, color: col, t0, dur: 0.3, font: o.font });
  }
  /* the highlighter behind one part of a centred line of handwriting */
  function hiPart(text, part, cx, y, size, t0, o = {}) {
    const i = text.indexOf(part); if (i < 0) return;
    const f = hand(size), x0 = cx - tw(text, f) / 2 + tw(text.slice(0, i), f), w = tw(part, f);
    NB.highlight(x0 - size * 0.08, y - size * 0.34, w + size * 0.16, size * 0.7, { t0, dur: o.dur || 0.3, color: o.color });
  }
  /* a claim sticky (Jay's), struck by the red pen at strikeT */
  function claim(text, x, y, t0, o = {}) {
    if (T < t0) return;
    const st = NB.sticky(text, x, y, { w: o.w || 300, rot: o.rot ?? -0.04, t0, size: o.size || 36 });
    if (o.strike != null && T >= o.strike) NB.strike(x + 12, y + st.h - 26, x + st.w - 12, y + 30, { t0: o.strike, dur: 0.3 });
    return st;
  }

  /* ---------------- ink pieces ---------------- */
  const coin = (x, y, r, o) => Levels.title.coin(x, y, r, o);
  /* an ink die: face 1–6 (pips), rolled by rot */
  const PIPS = { 1: [[0, 0]], 2: [[-1, -1], [1, 1]], 3: [[-1, -1], [0, 0], [1, 1]], 4: [[-1, -1], [1, -1], [-1, 1], [1, 1]],
    5: [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]], 6: [[-1, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [1, 1]] };
  function die(x, y, s, face, rot = 0) {
    g.save(); g.translate(x, y); g.rotate(rot);
    g.fillStyle = '#f7f5ee'; g.strokeStyle = PN.ink; g.lineWidth = 6; g.lineJoin = 'round';
    rrect(-s / 2, -s / 2, s, s, s * 0.18); g.fill(); g.stroke();
    g.fillStyle = PN.ink;
    for (const [px, py] of PIPS[face] || []) { g.beginPath(); g.arc(px * s * 0.26, py * s * 0.26, s * 0.085, 0, TAU); g.fill(); }
    g.restore();
  }
  /* the thought bubble from the coin */
  function bubble(text, x, y, from, t0) {
    if (T < t0) return;
    alpha(P(T, t0, t0 + 0.3), () => {
      g.save(); g.fillStyle = PN.page; g.strokeStyle = PN.ink; g.lineWidth = 4;
      const [fx, fy] = from;
      for (const [k, r] of [[0.25, 8], [0.5, 13]]) { g.beginPath(); g.arc(fx + (x - 140 - fx) * k, fy + (y + 30 - fy) * k, r, 0, TAU); g.fill(); g.stroke(); }
      g.beginPath(); g.ellipse(x, y, 180, 52, 0, 0, TAU); g.fill(); g.stroke();
      g.restore();
    });
    NB.write(text, x, y + 2, { size: 34, weight: 700, align: 'center', t0: t0 + 0.15, dur: 0.45 });
  }

  /* ---------------- the coin on the beat ----------------
     flip k (0-based) of a run lands on beat b0 + k: it rests showing its face for the first 35% of the beat after
     landing, then flies (up and down, turning twice: face-on again at the landing) to land flip k + 1 on the next beat. */
  function flipState(S, t, b0, n, face0 = 0) {
    const p = S.beatPos(t) - b0, k = Math.floor(p), u = p - k;
    if (p < -0.65) return { k: -1, flying: false, landed: 0 };
    if (u >= 0.35 && k + 1 < n) { const v = (u - 0.35) / 0.65; return { k: k + 1, flying: true, v, landed: Math.max(0, k + 1) }; }
    return { k: clamp(k, 0, n - 1), flying: false, landed: clamp(k + 1, 0, n) };
  }
  function beatCoin(S, t, b0, n, faceOf, x, y, r, peak) {
    const st = flipState(S, t, b0, n);
    if (st.flying) {
      const h = 4 * st.v * (1 - st.v);
      g.save(); g.strokeStyle = PN.pencil; g.lineWidth = 4; g.lineCap = 'round'; g.globalAlpha *= 0.7;
      for (let q = -1; q <= 1; q++) { g.beginPath(); g.moveTo(x - r - 22, y - peak * h + q * 22); g.lineTo(x - r - 46 - (q ? 0 : 12), y - peak * h + q * 22); g.stroke(); }
      g.restore();
      coin(x, y - peak * h, r, { turn: st.v * TAU * 2, face: faceOf(st.k) });
    } else coin(x, y, r, { face: st.k >= 0 ? faceOf(st.k) : 'H' });
    return st;
  }
  const HT = k => nums().coin.flips.outcomes[k] || 'H';

  /* ---------------- charts: a graph-paper panel, axes in ink, a dashed theory line ---------------- */
  /* C: {id, x, y (top), w, h, xMax, xTicks: [v], yMax: {n,d}, yTicks: [{n,d}], yLines (fractions of yMax), theory {n,d},
     xLabel, yLabel} */
  function chartOf(C) {
    C.X = v => C.x + (v / C.xMax) * C.w;
    C.Y = v => C.y + C.h - (v / Chance.toNumber(C.yMax)) * C.h;
    C.oy = C.y + C.h;
    return C;
  }
  function chartFrame(C, t0) {
    if (T < t0) return;
    alpha(P(T, t0, t0 + 0.3), () => art('jch|' + C.id, C.x - 4, C.y - 4, C.w + 8, C.h + 8, () => {
      g.fillStyle = PN.page; g.fillRect(C.x, C.y, C.w, C.h);
      g.strokeStyle = PN.gridMajor; g.lineWidth = 2; g.beginPath();
      for (const v of C.xGrid || C.xTicks) { const x = C.X(v); g.moveTo(x, C.y); g.lineTo(x, C.oy); }
      for (const v of C.yGrid || C.yTicks) { const y = C.Y(Chance.toNumber(v)); g.moveTo(C.x, y); g.lineTo(C.x + C.w, y); }
      g.stroke();
    }));
    const pr = E.outCubic(P(T, t0 + 0.1, t0 + 0.6));
    NB.pen([[C.x, C.oy], [C.x + C.w + 16, C.oy]], { w: 5, color: PN.ink, prog: pr, step: 10 });
    NB.pen([[C.x, C.oy], [C.x, C.y - 16]], { w: 5, color: PN.ink, prog: pr, step: 10 });
    const lt = t0 + 0.4, lab = C.lab || 24;
    NB.write('0', C.x - 14, C.oy, { font: 'ui', size: lab, weight: 700, align: 'right', t0: lt, dur: 0.1 });
    C.yTicks.forEach((v, j) => NB.write(ft(v), C.x - 14, C.Y(Chance.toNumber(v)), { font: 'ui', size: lab, weight: 700, align: 'right', t0: lt + j * 0.03, dur: 0.12 }));
    C.xTicks.forEach((v, i) => NB.write(String(v), C.X(v), C.oy + 24, { font: 'ui', size: lab, weight: 700, align: 'center', t0: lt + i * 0.03, dur: 0.12 }));
    if (C.xLabel) NB.write(C.xLabel, C.x + C.w + 16, C.oy + 56, { font: 'ui', size: lab - 2, weight: 700, color: PN.pencil, align: 'right', t0: lt + 0.2, dur: 0.3 });
    if (C.yLabel) { g.save(); g.translate(C.x - (C.yLabW || 78), C.y + C.h / 2); g.rotate(-Math.PI / 2); NB.write(C.yLabel, 0, 0, { font: 'ui', size: lab - 2, weight: 700, color: PN.pencil, align: 'center', t0: lt + 0.2, dur: 0.3 }); g.restore(); }
    // the theory: a dashed red line at P, labelled at its right end
    if (C.theory) {
      const y = C.Y(Chance.toNumber(C.theory)), tp = E.outCubic(P(T, lt + 0.2, lt + 0.7));
      g.save(); g.setLineDash([14, 12]);
      NB.pen([[C.x, y], [C.x + C.w, y]], { w: 4, color: PN.redpen, prog: tp, step: 10 });
      g.restore();
      plabel(C.theoryText || ft(C.theory), C.x + C.w + 12, y, lt + 0.6, { size: 30, color: PN.redpen });
    }
  }
  /* the running share after trials 1..m of a simulation with running hits, clipped to the chart; dots when few */
  function chartSeries(C, run, m, o = {}) {
    if (m < 1) return;
    g.save(); g.beginPath(); g.rect(C.x - 2, C.y - 14, C.w + 16, C.h + 16); g.clip();
    g.strokeStyle = o.color || PN.ink; g.lineWidth = o.w || 4; g.lineJoin = 'round'; g.lineCap = 'round';
    const stride = m > 400 ? 2 : 1;
    g.beginPath();
    for (let i = 0; i < m; i += stride) { const x = C.X(i + 1), y = C.Y(run[i] / (i + 1)); if (i) g.lineTo(x, y); else g.moveTo(x, y); }
    if ((m - 1) % stride) g.lineTo(C.X(m), C.Y(run[m - 1] / m));
    g.stroke();
    if (o.dots) { g.fillStyle = o.color || PN.ink; for (let i = 0; i < m; i++) { g.beginPath(); g.arc(C.X(i + 1), C.Y(run[i] / (i + 1)), o.dots, 0, TAU); g.fill(); } }
    g.restore();
  }
  const ONE = () => Chance.frac(1), HALF = () => Chance.frac('1/2'), QUARTS = () => ['1/4', '1/2', '3/4', '1'].map(Chance.frac);
  let CHARTS = null;
  function charts() {
    if (CHARTS) return CHARTS;
    const N = nums(), eighths = [1, 2, 3, 4, 5, 6, 7, 8].map(k => Chance.frac(`${k}/8`));
    const heads = { yMax: ONE(), yTicks: QUARTS(), theory: N.coin.pH, theoryText: 'theory ' + ft(N.coin.pH), yLabel: 'share of heads' };
    const socks = { yMax: ONE(), yTicks: QUARTS(), yGrid: eighths, theory: N.wheel.p, theoryText: ft(N.wheel.p), yLabel: 'share of FREE SOCKS', h: 380 };
    CHARTS = {
      c1: chartOf({ id: 'c1', x: 870, y: 400, w: 600, h: 330, xMax: 16, xTicks: [4, 8, 12, 16], xGrid: [2, 4, 6, 8, 10, 12, 14, 16], xLabel: 'FLIPS', ...heads }),
      c2: chartOf({ id: 'c2', x: 870, y: 400, w: 600, h: 330, xMax: FLIPS, xTicks: [250, 500, 750, 1000], xLabel: 'FLIPS', ...heads }),
      b8: chartOf({ id: 'b8', x: 570, y: 360, w: 360, xMax: 8, xTicks: [2, 4, 6, 8], xGrid: [1, 2, 3, 4, 5, 6, 7, 8], xLabel: 'SPINS', ...socks }),
      b800: chartOf({ id: 'b800', x: 1080, y: 360, w: 360, xMax: N.wheel.big.n, xTicks: [200, 400, 600, 800], xLabel: 'SPINS', ...socks, yLabel: null }),
    };
    return CHARTS;
  }

  /* ======================================================================
     act: intro — the game-show marquee, the title, a coin spinning on the beat
     ====================================================================== */
  function marquee(S, t, x, y, w, h) {
    const on = Math.floor(S.beatPos(t)) & 1, n = 2 * Math.round(w / 70) + 2 * Math.round(h / 70);
    const per = 2 * (w + h), pt = s => { s = ((s % per) + per) % per; if (s < w) return [x + s, y]; s -= w; if (s < h) return [x + w, y + s]; s -= h; if (s < w) return [x + w - s, y + h]; s -= w; return [x, y + h - s]; };
    g.save(); g.strokeStyle = PN.ink; g.lineWidth = 6; rrect(x, y, w, h, 26); g.stroke();
    for (let i = 0; i < n; i++) {
      const [bx, by] = pt(i * per / n), lit = (i & 1) === on && t > 0.2;
      g.beginPath(); g.arc(bx, by, 13, 0, TAU); g.fillStyle = lit ? '#f6d44a' : PN.page; g.fill(); g.lineWidth = 3; g.stroke();
    }
    g.restore();
  }
  function actIntro(S, t, A) {
    NB.ground();
    marquee(S, t, 430, 215, 1060, 270);
    NB.title(TITLE, 960, 350, { size: 96, t0: 0.15, hi: 'Remember' });
    // the coin spins in the air on the beat (no landing: no outcome is shown before the song starts flipping)
    const pu = S.pulse(t, 5), p = S.beatPos(t);
    coin(960, 620 - 24 * pu, 70, { turn: Math.max(0, p) * Math.PI, face: 'H' });
    const L = A.lines, tG = L.length ? S.W(L[0], 'game') : 1, tC = L.length ? S.W(L[0], 'chance') : 1.5;
    if (t > tG) tagCard('THE GAME OF CHANCE', 960, 760, tG);
    if (t > tC) NB.note('step right up!', 1330, 610, -0.05, { t0: tC, size: 40, align: 'center' });
  }
  /* a paper price-tag-style card with ui text (no string) */
  function tagCard(text, x, y, t0, o = {}) {
    const size = o.size || 40, w = tw(text, uif(size)) + 60, h = size * 1.7, s = E.outBack(P(T, t0, t0 + 0.3), 1.6);
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.03); g.scale(s, s);
    g.fillStyle = PN.redpen; rrect(-w / 2, -h / 2, w, h, 12); g.fill();
    g.fillStyle = '#fff'; g.font = uif(size); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, 0, 2);
    g.restore();
  }

  /* ======================================================================
     act: verse 1 — five heads, "tails is due?", no memory, 1 out of 2, 1/2 every flip
     ====================================================================== */
  const V1 = { coin: { x: 590, y: 720, r: 58, peak: 210 }, tally: { x: 610, y: 330, dx: 88 }, sticky: { x: 1130, y: 250 } };
  function actVerse1(S, t, A) {
    NB.ground();
    const N = nums().coin, L = A.lines, K = V1.coin, TL = V1.tally, n = N.streak;
    // the coin flips on the beat from "Five": flip k lands on beat b0 + k (the simulation's flips 1..n, all H)
    const b0 = S.beatAfter(S.W(L[0], 'Five')), st = beatCoin(S, t, b0, n, HT, K.x, K.y, K.r, K.peak);
    const landAt = k => S.off + (b0 + k) * S.bt;
    NB.write('FLIPS', 470, TL.y + 4, { font: 'ui', size: 26, weight: 700, color: PN.pencil, t0: A.at + 0.1, dur: 0.3 });
    for (let k = 0; k < n; k++) NB.write(HT(k), TL.x + k * TL.dx, TL.y, { size: 76, weight: 700, align: 'center', t0: landAt(k), dur: 0.12 });
    const tDue = S.W(L[0], 'due');
    NB.write('?', TL.x + n * TL.dx, TL.y, { size: 76, weight: 700, color: PN.redpen, align: 'center', t0: tDue, dur: 0.2 });
    // Jay's sticky, struck on "can't"
    claim('TAILS IS DUE?', V1.sticky.x, V1.sticky.y, tDue, { strike: S.W(L[1], 'can'), rot: 0.03 });
    // "can't remember": the coin's thought bubble
    bubble('I have no memory.', 900, 560, [K.x + 40, K.y - 50], S.W(L[1], 'remember'));
    // "Heads or tails, it's one out of two"
    const tH = S.W(L[2], 'Heads'), tT = S.W(L[2], 'tails'), tO = S.W(L[2], 'one'), tTw = S.W(L[2], 'two');
    const cy = 540;
    if (t > tH) { const s = pop(t, tH, 0.25, 1.6); g.save(); g.translate(1200, cy); g.scale(s, s); coin(0, 0, 50, { face: 'H' }); g.restore(); }
    if (t > tT) { const s = pop(t, tT, 0.25, 1.6); g.save(); g.translate(1340, cy); g.scale(s, s); coin(0, 0, 50, { face: 'T' }); g.restore(); }
    if (t > tO) say(`HEADS OR TAILS: ${N.tails} OUT OF ${N.n}`, 1230, 650, tO, { size: 44, dur: Math.max(0.4, tTw + 0.2 - tO) });
    if (t > tTw) { const eq = `P(T) = ${ft(N.pT)}`; say(eq, 1270, 730, tTw, { size: 60 }); hiPart(eq, ft(N.pT), 1270, 730, 60, tTw + 0.3); }
    // "Every flip starts over": 1/2 under every flip; the red one under the "?" on "truth"
    const tE = S.W(L[3], 'Every'), tOv = S.W(L[3], 'over'), tTr = S.W(L[3], 'truth');
    for (let k = 0; k < n; k++) NB.write(ft(N.pH), TL.x + k * TL.dx, TL.y + 64, { size: 30, weight: 700, color: PN.pencil, align: 'center', t0: tE + (tOv - tE) * k / n, dur: 0.2 });
    NB.write(ft(N.pT), TL.x + n * TL.dx, TL.y + 64, { size: 34, weight: 700, color: PN.redpen, align: 'center', t0: tTr, dur: 0.25 });
    if (t > tTr) NB.circle(TL.x + n * TL.dx, TL.y + 30, 44, 72, { t0: tTr + 0.2, dur: 0.35 });
    void st;
  }

  /* ======================================================================
     act: chorus — the hook big; the coin flips on the beat; the tally chart; the die rolls from "Dice"
     ====================================================================== */
  const CHO = { coin: { x: 620, y: 700, r: 64, peak: 200 }, die: { x: 470, y: 420, s: 92 }, hookY: 285 };
  /* the flips a chorus shows: chorus 1 = flips 1..16 one per beat from its first word; chorus 2 = the 1000 flips drawn
     on over the chorus (the coin keeps flipping the next simulated flips on the beat) */
  function chorusRun(S, A) {
    const L = A.lines, t0 = S.W(L[0], 'Coins'), tEnd = S.E(L[L.length - 1]), tLast = S.S(L[L.length - 1]);
    const b0 = S.beatAfter(t0), tb = S.off + b0 * S.bt;
    const beats = Math.max(4, Math.floor((tEnd - tb) / S.bt)), beats2 = Math.max(4, Math.floor((tLast - tb) / S.bt));
    return { t0, tEnd, tLast, b0, beats, beats2 };
  }
  function flipsAt(S, p, t) {
    const out = {};
    for (const id of ['chorus1', 'chorus2']) {
      const A = p.acts.find(a => a.id === id); if (!A) continue;
      const R = chorusRun(S, A);
      if (id === 'chorus1') { const n = Math.min(16, R.beats + 1); out[id] = { n, landed: flipState(S, t, R.b0, n).landed }; }
      else out[id] = { n: FLIPS, landed: Math.floor(S.onBeats(t, R.t0, R.beats2) * FLIPS) };
    }
    return out;
  }
  function actChorus(S, t, A) {
    NB.ground();
    const N = nums(), first = A.n === 1, L = A.lines, R = chorusRun(S, A), run = N.coin.flips.running;
    const C = first ? charts().c1 : charts().c2;
    // the hook line, big, written as it is sung (the current chorus line)
    let cur = -1; L.forEach((i, k) => { if (t >= S.S(i) - 0.05) cur = k; });
    if (cur >= 0) {
      const i = L[cur], text = S.lines[i].text, ws = S.lines[i].words, size = 92;
      const w = tw(text, hand(size)), sz = w > 1100 ? Math.floor(size * 1100 / w) : size;
      NB.write(text, 960, CHO.hookY, { size: sz, weight: 700, align: 'center', color: PN.redpen, t0: S.S(i) - 0.05, dur: Math.max(0.3, ws[ws.length - 1].s + 0.2 - S.S(i)) });
    }
    // the chart
    chartFrame(C, A.at + 0.1);
    let landed, n;
    if (first) {
      n = Math.min(16, R.beats + 1);
      const st = beatCoin(S, t, R.b0, n, HT, CHO.coin.x, CHO.coin.y, CHO.coin.r, CHO.coin.peak);
      landed = st.landed;
      chartSeries(C, run, landed, { dots: 7 });
      for (let k = 0; k < landed; k++) NB.write(HT(k), C.X(k + 1), C.oy + 60, { size: 26, weight: 700, color: HT(k) === 'H' ? PN.ink : PN.redpen, align: 'center', t0: -1e9 });
    } else {
      n = FLIPS;
      landed = Math.floor(S.onBeats(t, R.t0, R.beats2) * FLIPS);
      beatCoin(S, t, R.b0, R.beats + 1, k => HT(16 + k), CHO.coin.x, CHO.coin.y, CHO.coin.r, CHO.coin.peak);
      chartSeries(C, run, landed, { w: 4 });
    }
    if (landed > 0) {
      const h = run[landed - 1];
      plabel(`heads: ${h} of ${landed}`, C.x + 10, C.y - 34, A.at + 0.2, { size: 32 });
    }
    // "settles near 1/2" once chorus 2 has drawn its 1000 flips
    if (!first && landed >= FLIPS) NB.note(`settles near ${ft(N.coin.pH)}`, C.x + C.w - 20, C.y + 60, -0.03, { t0: R.tLast, size: 38, align: 'right' });
    if (first && landed >= N.coin.streak + 3) NB.note('wobbles', C.x + 330, C.y + 40, -0.03, { t0: S.S(L[Math.min(2, L.length - 1)]), size: 36 });
    // "Dice don't keep score!": the die rolls on the beat from "Dice" (its faces: the seeded rolls)
    const tD = L.length > 1 ? S.W(L[1], 'Dice') : 1e6;
    if (t > tD - 0.1) {
      const bd = S.beatAfter(tD), p = S.beatPos(t) - bd, k = Math.max(0, Math.floor(p)), u = p - Math.floor(p);
      const faces = N.die.faces, face = faces[(k + (first ? 0 : 32)) % faces.length], hop = p >= 0 && u > 0.55 ? Math.sin((u - 0.55) / 0.45 * Math.PI) : 0;
      const s = pop(t, tD - 0.1, 0.25, 1.6);
      g.save(); g.translate(CHO.die.x, CHO.die.y - 50 * hop); g.scale(s, s);
      die(0, 0, CHO.die.s, face, hop * 1.2);
      g.restore();
      if (t > tD + 0.3) NB.write('no memory', CHO.die.x, CHO.die.y + 84, { size: 30, weight: 700, color: PN.pencil, align: 'center', t0: tD + 0.3, dur: 0.3 });
    }
  }

  /* ======================================================================
     act: verse 2 — the gumball jar; P(red) = 3/4; favorable ÷ total; the 0–1 likelihood line
     ====================================================================== */
  const JAR = { x: 480, y: 290, w: 250, h: 300 };
  const BALL_POS = [[530, 546], [608, 546], [569, 478], [686, 546]];
  const BALL_COLOR = { RED: '#ef6a1f', BLUE: '#2f5fb3' };
  const LINE = { x0: 520, x1: 1420, y: 740 };
  const lx = v => LINE.x0 + (LINE.x1 - LINE.x0) * Chance.toNumber(v);
  function jar() {
    const J = JAR;
    art('jg-jar', J.x - 10, J.y - 50, J.w + 20, J.h + 64, () => {
      g.save(); g.lineJoin = 'round'; g.strokeStyle = PN.ink;
      g.fillStyle = '#e8e4d8'; g.lineWidth = 5;
      rrect(J.x + 30, J.y - 30, J.w - 60, 34, 8); g.fill(); g.stroke();
      rrect(J.x + J.w / 2 - 22, J.y - 48, 44, 20, 6); g.fill(); g.stroke();
      g.fillStyle = 'rgba(206,226,238,0.35)'; g.lineWidth = 6;
      rrect(J.x, J.y + 4, J.w, J.h - 4, 46); g.fill(); g.stroke();
      g.strokeStyle = 'rgba(255,255,255,0.9)'; g.lineWidth = 8; g.lineCap = 'round';
      g.beginPath(); g.moveTo(J.x + 30, J.y + 70); g.lineTo(J.x + 30, J.y + 180); g.stroke();
      g.restore();
    });
  }
  function gumball(x, y, r, color, s) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    g.fillStyle = color; g.strokeStyle = PN.ink; g.lineWidth = 4;
    g.beginPath(); g.arc(0, 0, r, 0, TAU); g.fill(); g.stroke();
    g.fillStyle = 'rgba(255,255,255,0.7)'; g.beginPath(); g.ellipse(-r * 0.35, -r * 0.4, r * 0.28, r * 0.16, -0.6, 0, TAU); g.fill();
    g.restore();
  }
  function actVerse2(S, t, A) {
    NB.ground();
    const N = nums().jar, L = A.lines;
    // "Three red gumballs and a single blue": the jar; the red ones pop in on "red", the blue on "blue"
    const tJ = A.at + 0.1, tR = S.W(L[0], 'red'), tB = S.W(L[0], 'blue');
    if (t > tJ) alpha(P(t, tJ, tJ + 0.3), jar);
    let r = 0;
    N.balls.forEach((k, i) => { const [x, y] = BALL_POS[i], t0 = k === 'RED' ? tR + 0.12 * r++ : tB; gumball(x, y, 36, BALL_COLOR[k] || PN.pencil, pop(t, t0, 0.3, 1.8)); });
    // "Count the gumballs, not the colors": 1–4 on each ball; Jay's "2 COLORS = 50/50?" struck on "colors"
    const tC = S.W(L[1], 'gumballs'), tCo = S.W(L[1], 'colors');
    N.balls.forEach((k, i) => { const [x, y] = BALL_POS[i]; NB.write(String(i + 1), x + 3, y + 4, { font: 'ui', size: 34, weight: 700, color: '#fff', align: 'center', t0: tC + i * 0.15, dur: 0.12 }); });
    claim(`${N.colors} COLORS\n= 50/50?`, 790, 250, S.W(L[1], 'not'), { strike: tCo + 0.2, size: 34, w: 250, rot: -0.04 });
    // "three out of four is true": P(red) = 3/4 = 0.75 = 75%
    const tTh = S.W(L[1], 'three'), tF = S.W(L[1], 'four'), tTr = S.W(L[1], 'true');
    if (t > tTh) { const eq = `P(red) = ${ft(N.pRed)}`; say(eq, 1260, 300, tTh, { size: 70, dur: Math.max(0.4, tF + 0.2 - tTh) }); hiPart(eq, ft(N.pRed), 1260, 300, 70, tTr); }
    if (t > tTr) say(`= ${N.decpct}`, 1260, 385, tTr, { size: 50, color: PN.pencil });
    if (tR < 1e5 && t > tTh) N.balls.forEach((k, i) => { if (k === 'RED') { const [x, y] = BALL_POS[i]; multiply(() => NB.circle(x, y, 44, 44, { t0: tTh + i * 0.1, color: PN.hi, w: 12, dur: 0.4 })); } });
    // "Favorable over total": P = favorable ÷ total
    const tFa = S.W(L[2], 'Favorable'), tTo = S.W(L[2], 'total');
    if (t > tFa) { const eq = 'P = favorable ÷ total'; say(eq, 1200, 520, tFa, { size: 54, dur: Math.max(0.4, tTo + 0.3 - tFa) }); }
    if (t > tTo) NB.note(`${N.red} red ÷ ${N.n} gumballs`, 1200, 590, -0.02, { t0: tTo + 0.2, size: 36, align: 'center' });
    // "zero up to one": the likelihood line; "Zero is impossible, and certain is one"
    const tZ = S.W(L[2], 'zero'), tOne = S.W(L[2], 'one');
    if (t > tZ) {
      NB.pen([[LINE.x0, LINE.y], [LINE.x1, LINE.y]], { w: 5, color: PN.ink, prog: E.outCubic(P(t, tZ, Math.max(tZ + 0.4, tOne))), step: 10 });
      const tick = (v, t0) => { if (t >= t0) NB.pen([[lx(v), LINE.y - 14], [lx(v), LINE.y + 14]], { w: 5, color: PN.ink, prog: E.outCubic(P(t, t0, t0 + 0.2)) }); };
      const zero = Chance.frac(0), one = Chance.frac(1), half = HALF();
      const tI = S.W(L[3], 'impossible'), tCe = S.W(L[3], 'certain'), tO2 = S.W(L[3], 'one');
      [[zero, tZ], [one, tOne], [half, tCe]].forEach(([v, t0]) => { tick(v, t0); NB.write(ft(v), lx(v), LINE.y + 38, { size: 32, weight: 700, align: 'center', t0, dur: 0.2 }); });
      const wy = LINE.y + 78;
      [[zero, 'IMPOSSIBLE', tI], [half, 'EQUALLY LIKELY', tCe + 0.3], [one, 'CERTAIN', tCe]].forEach(([v, w, t0]) =>
        NB.write(w, lx(v), wy, { font: 'ui', size: 24, weight: 700, color: PN.ink, align: 'center', t0, dur: 0.3 }));
      // P(red) lands on the line on the last "one": its word from Chance.likelihood
      const tD = Math.min(tO2 + 0.15, tCe + 0.4);                     // before the page turns
      if (t >= tD) { const s = pop(t, tD, 0.25, 1.8); g.fillStyle = BALL_COLOR.RED; g.strokeStyle = PN.ink; g.lineWidth = 3; g.beginPath(); g.arc(lx(N.pRed), LINE.y, 14 * s, 0, TAU); g.fill(); g.stroke(); }
      plabel(`P(red) = ${ft(N.pRed)}: ${N.word}`, lx(N.pRed), LINE.y - 46, tD + 0.2, { size: 30, align: 'center' });
    }
  }

  /* ======================================================================
     act: bridge — 8 spins wobble, 800 spins settle (the video's own runs)
     ====================================================================== */
  function actBridge(S, t, A) {
    NB.ground();
    const W = nums().wheel, L = A.lines, B8 = charts().b8, B800 = charts().b800;
    const tS1 = S.W(L[0], 'Spin'), tWo = S.W(L[0], 'wobbles');
    const tS2 = L.length > 1 ? S.W(L[1], 'Spin') : 1e6, tSe = L.length > 1 ? S.W(L[1], 'settles') : 1e6;
    NB.write('SPIN IT 8 TIMES', B8.x + B8.w / 2, B8.y - 70, { font: 'ui', size: 30, weight: 700, align: 'center', t0: tS1, dur: 0.3 });
    NB.write('SPIN IT 800 TIMES', B800.x + B800.w / 2, B800.y - 70, { font: 'ui', size: 30, weight: 700, align: 'center', t0: tS2, dur: 0.3 });
    chartFrame(B8, A.at + 0.1);
    if (t > tS2 - 0.4) chartFrame(B800, tS2 - 0.4);
    // 8 spins, one a beat from the first "Spin"
    const m8 = Math.floor(S.onBeats(t, tS1, W.small.n) * W.small.n + 1e-6);
    chartSeries(B8, W.small.running, m8, { dots: 8 });
    if (t > tWo) {
      plabel(`${W.small.x} of ${W.small.n} = ${ft(W.smallShare)}`, B8.x + B8.w - 12, B8.y + 44, tWo, { size: 32, align: 'right' });
      NB.note('wobbles!', B8.x + B8.w - 40, B8.y + 110, -0.05, { t0: tWo + 0.2, size: 40, align: 'right' });
    }
    // 800 spins, drawn on over 8 beats from the second "Spin"
    const m800 = Math.floor(S.onBeats(t, tS2, Math.max(3, Math.round((tSe - tS2) / S.bt))) * W.big.n);   // done on "settles"
    chartSeries(B800, W.big.running, m800, { w: 4 });
    if (t > tSe) {
      const share = Math.round(Chance.toNumber(W.bigShare) * 1000) / 1000;
      plabel(`${W.big.x} of ${W.big.n}: about ${share}`, B800.x + B800.w - 12, B800.y + 44, tSe, { size: 32, align: 'right' });
      NB.note(`settles near ${ft(W.p)}`, B800.x + B800.w - 20, B800.y + 110, -0.04, { t0: tSe + 0.2, size: 40, align: 'right' });
    }
  }

  /* ======================================================================
     act: outro — the hook big; the rules; LUCKY
     ====================================================================== */
  const RULES = ['P = favorable ÷ total (from 0 to 1)', 'every flip is brand new: nothing is ever due', 'small runs wobble, big runs settle near the theory'];
  function actOutro(S, t, A) {
    NB.ground();
    const L = A.lines, tC = S.WA(L, 'Coins'), tN = S.WA(L, 'Nothings'), tDue = S.WA(L, 'due');
    if (t > tC - 0.1) NB.title("Coins don't remember!", 960, 275, { size: 88, t0: tC - 0.1, hi: 'remember!' });
    if (t > tN - 0.1) say("Nothing's ever due!", 960, 395, tN - 0.1, { size: 84, color: PN.redpen, dur: Math.max(0.4, tDue + 0.3 - tN) });
    const tR = tN + 0.3;
    if (t > tR) {
      const x = 470, y = 480, w = 980, h = 260;
      const s = E.outBack(P(t, tR, tR + 0.4), 1.3);
      g.save(); g.translate(x + w / 2, y + h / 2); g.scale(s, s); g.translate(-(x + w / 2), -(y + h / 2));
      NB.indexCard(x, y, w, h, { header: 64, pitch: 62 });
      NB.write('the rules of chance', x + 30, y + 34, { font: 'ui', size: 30, weight: 700, t0: tR, dur: 0.4 });
      RULES.forEach((r, i) => NB.write(r, x + 40, y + 64 + 62 * i + 32, { size: 38, weight: i === 1 ? 700 : 400, color: i === 1 ? PN.redpen : PN.ink, t0: tR + 0.4 + i * 0.4, dur: 0.4 }));
      g.restore();
    }
    const tSt = Math.min(tDue + 0.3, S.dur - 0.8);                     // LUCKY stamps on "due!"
    if (t > tSt) UI.stamp(STAMP, 1560, 420, { s: E.outBack(P(t, tSt, tSt + 0.3), 1.6), rot: -0.12 });
  }

  /* ---------------- the acts' plan (by section, from the take's own line times) ---------------- */
  function plan(S) {
    const b = i => (i > 0 ? Math.max(S.E(i - 1) - 0.1, S.S(i) - 0.35) : 0);   // never before the previous line's last word
    const acts = [];
    const add = (id, draw, lines, o = {}) => { if (id !== 'intro' && !lines.length) return; acts.push({ id, draw, lines, at: id === 'intro' ? 0 : b(lines[0]), ...o }); };
    add('intro', actIntro, S.section('Intro'));
    add('verse1', actVerse1, S.section('Verse 1'));
    add('chorus1', actChorus, S.section('Chorus', 1), { n: 1 });
    add('verse2', actVerse2, S.section('Verse 2'));
    add('bridge', actBridge, S.section('Bridge'));
    add('chorus2', actChorus, S.section('Chorus', 2), { n: 2 });
    add('outro', actOutro, S.section('Outro'));
    acts.sort((a, c) => a.at - c.at);
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur; });
    return { acts, ticks: acts.slice(1).map(a => a.at) };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }
  /* the notebook page turn: the old page slides up off the top (the next page is already underneath) */
  function turn(drawOld, drawNew, t, t0, dur) {
    const p = E.inOutCubic(P(t, t0, t0 + dur));
    drawNew();
    if (p >= 1) return;
    const dy = -1100 * p;
    g.save(); g.beginPath(); g.rect(0, 0, 1920, 1080 + dy); g.clip(); g.translate(0, dy); drawOld(); g.restore();
    g.save(); g.fillStyle = 'rgba(29,43,79,0.12)'; g.fillRect(0, 1080 + dy, 1920, 8); g.restore();
  }
  function drawActs(S, p, t) {
    const k = actAt(p, t), A = p.acts[k];
    const run = i => () => { g.save(); p.acts[i].draw(S, t, p.acts[i]); g.restore(); g.globalAlpha = 1; };
    if (k > 0 && t < A.at + TURN) turn(run(k - 1), run(k), t, A.at, TURN);
    else run(k)();
  }

  /* ---------------- the cast: Jay (left) and Ms. Rivera (right), bobbing on the beat ---------------- */
  const POSE_DX = { rivera_point: -131.5 / 1100, jay_point: 48.5 / 1100 };     // as levels/video.js (the body stays put)
  function puppet(name, x, y, h, pose, facing, env, t, rot) {
    const img = (Puppet.POSES[name] || {})[pose] || Puppet.DEFAULT[name] || name, native = Puppet.NATIVE[img] || 0;
    const flip = native !== 0 && Math.sign(native) !== Math.sign(facing);
    Puppet.draw(name, x - (POSE_DX[img] || 0) * h * (flip ? -1 : 1), y, h, { env, facing, t, pose, rot });
  }
  function pointCues(S, p) {
    if (p._pts) return p._pts;
    const jay = [], riv = [], A = id => p.acts.find(a => a.id === id), L = id => (A(id) ? A(id).lines : []);
    for (const id of ['chorus1', 'chorus2']) { const l = L(id); if (l.length >= 4) { jay.push(S.W(l[1], 'Dice')); riv.push(S.W(l[0], 'remember'), S.W(l[2], 'brand')); } }
    const v1 = L('verse1'), v2 = L('verse2'), br = L('bridge'), out = L('outro');
    if (v1.length >= 4) { jay.push(S.W(v1[0], 'tails')); riv.push(S.W(v1[1], 'remember'), S.W(v1[2], 'one')); }
    if (v2.length >= 4) { jay.push(S.W(v2[1], 'not')); riv.push(S.W(v2[1], 'three'), S.W(v2[2], 'Favorable')); }
    if (br.length >= 2) { jay.push(S.W(br[0], 'wobbles')); riv.push(S.W(br[1], 'settles')); }
    if (out.length) { jay.push(S.WA(out, 'Coins')); riv.push(S.WA(out, 'due')); }
    return (p._pts = { jay, riv });
  }
  function cast(S, p, t, docked) {
    const pu = S.pulse(t, 7), bar = Math.floor(S.beatPos(t) / 4), sway = (bar % 2 ? 1 : -1) * 0.02, bob = 10 * pu;
    const cues = pointCues(S, p), near = arr => arr.some(x => t > x && t < x + 1.0);
    const y = docked ? 1080 : BAND_Y + 20, h = docked ? 380 : 330;
    puppet('jay', 250, y - bob, h, near(cues.jay) ? 'point' : null, 1, 0.15 + 0.85 * pu, t, sway);
    puppet('rivera', 1740, y - bob, h * 1.05, near(cues.riv) ? 'point' : null, -1, 0.15 + 0.85 * pu, t, -sway);
  }

  /* ---------------- karaoke ---------------- */
  /* the hook words (red pen) in the choruses and the outro */
  const HOOKS = [['coins', 'dont', 'remember'], ['dice', 'dont', 'keep', 'score'], ['every', 'flip', 'is', 'brand', 'new'], ['like', 'the', 'one', 'before'], ['nothings', 'ever', 'due']];
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    if (item.section !== 'Chorus' && item.section !== 'Outro') return out;
    for (const h of HOOKS) for (let k = 0; k + h.length <= ws.length; k++) if (h.every((w, j) => normW(ws[k + j].w) === w)) h.forEach((_, j) => out.add(k + j));
    return out;
  }
  const layouts = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (layouts.has(key)) return layouts.get(key);
    const f = hand(size), sp = tw(' ', f), hk = hookIdx(item);
    let x = 0;
    const toks = item.words.map((w, j) => { const ww = tw(w.w, f), tk = { text: w.w, s: w.s, e: w.e, x, w: ww, hook: hk.has(j) }; x += ww + sp; return tk; });
    const out = { toks, width: x - sp, size };
    if (fontOk()) layouts.set(key, out);
    return out;
  }
  const wordEnd = (ws, j) => { const nx = ws[j + 1]; return Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1640) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25));
    const yy = y + (1 - a) * 30;
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < wordEnd(Lo.toks, j);
      if (active) NB.highlight(x - 8, yy - size * 0.42, tk.w + 16, size * 0.84, { t0: tk.s - 0.02, dur: 0.12 });
      if (!sung || t < tk.s + 0.3) NB.write(tk.text, x, yy, { size, weight: 700, color: PN.pencilLight, alpha: a });
      if (sung) NB.write(tk.text, x, yy, { size, weight: 700, color: tk.hook ? PN.redpen : PN.ink, t0: tk.s, dur: clamp(tk.e - tk.s, 0.08, 0.3) });
    });
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;       // a long instrumental gap: clear the line
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  /* the word being sung at t (QA: the karaoke's active word) */
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) if (t >= ws[j].s - 0.02 && t < wordEnd(ws, j)) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e };
    return null;
  }
  const ENDNOTE = "Coins don't remember. Nothing's ever due.";
  function band(song, t) {
    NB.indexCard(40, BAND_Y, 1840, 260, { header: 44, pitch: 78 });
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) NB.write(sec.toUpperCase(), 70, BAND_Y + 23, { font: 'ui', size: 22, weight: 700, color: PN.pencil });
    if (cur) drawCurrent(cur, t, CUR_Y);
    if (next) {
      let size = NEXT_SIZE; const w = tw(next.text, hand(size, 400));
      if (w > 1640) size = Math.floor(size * 1640 / w);
      NB.write(next.text, 960, NEXT_Y, { size, weight: 400, color: PN.pencil, align: 'center', alpha: 0.8 });
    } else if (song.items.length && t > song.items[song.items.length - 1].e) {
      NB.write(ENDNOTE, 960, NEXT_Y, { size: 40, weight: 700, color: PN.pencil, align: 'center', t0: song.items[song.items.length - 1].e + 0.3 });
    }
    if (!cur && !next && t > 0.6) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      ['♪', '♫', '♪'].forEach((n, i) => { g.save(); g.font = `64px 'Segoe UI Symbol', 'DejaVu Sans', sans-serif`; g.fillStyle = PN.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(n, 860 + i * 100, CUR_Y - 22 * pu * (i === b ? 1 : 0.15)); g.restore(); });
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class Jingle extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      try { this.nums = nums(); } catch (e) { this.err = 'chance: ' + e.message; console.warn('[jingle]', this.err); }
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[jingle] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); }, e => { console.warn('[jingle] no audio', e && e.message); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    back() { Game.replace(new Levels.map()); }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; }
    /* stamp the step now (QA / integration; ≥ 90% heard does this at the end, or the Finish button) */
    done() { if (this.completed) return; this.completed = true; this.pause(); if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    canFinish() { return !!this.song && !this.completed && this.heard >= FINISH_AT * this.song.dur; }
    finish() { if (this.canFinish()) { Audio.play('stamp', { gain: 0.6 }); this.done(); } }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      if (this.now() >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= FINISH_AT * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) this.back();
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.canFinish() && this._finBox && Math.abs(x - this._finBox.x) < this._finBox.w / 2 && Math.abs(y - this._finBox.y) < this._finBox.h / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.jingle && Game.content.jingle.title) || TITLE;
      if (!this.song || !this.nums) {
        NB.ground();
        NB.title(title, 960, 420, { size: 110, t0: this.enteredAt + 0.2, hi: 'Remember' });
        const msg = this.err ? 'the jingle is missing (' + this.err + ')' : 'warming up the band…';
        NB.write(msg, 960, 580, { font: 'ui', size: 44, color: PN.pencil, align: 'center', t0: this.enteredAt + 0.4 });
        if (docked) { Dock.head(title, ''); Dock.text('jg-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawActs(this.song, this.plan, t);
        cast(this.song, this.plan, t, docked);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hk = hookIdx(cur);
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? ENDNOTE : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the jingle…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.canFinish() && !this.ended) Dock.buttons('jg-fin', [{ label: 'Finish ✓', color: PN.redpen, onClick: () => this.finish() }]);
      if (this.ended && Game.current() === this) {
        Dock.buttons('jg-end', [{ label: '↺ Play it again', onClick: () => this.restart() },
          { label: 'Back to the map', color: PN.ink, onClick: () => this.back() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'jingle position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the jingle from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => this.back() });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused ▶: a white wash and an ink circle */
    pausedMark(y, r) {
      g.save(); g.fillStyle = 'rgba(252,252,248,0.55)'; g.fillRect(0, 0, 1920, UI.docked() ? 1080 : BAND_Y); g.restore();
      g.save(); g.fillStyle = PN.ink; g.beginPath(); g.arc(960, y, r, 0, TAU); g.fill();
      g.fillStyle = PN.page; g.beginPath(); g.moveTo(960 - r * 0.3, y - r * 0.52); g.lineTo(960 + r * 0.52, y); g.lineTo(960 - r * 0.3, y + r * 0.52); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song;
      const intro = t == null || (this.plan && this.plan.acts.length > 1 && t < this.plan.acts[1].at);   // the intro page writes the big title itself
      if (!intro) NB.write(TITLE, 190, 124, { size: 46, weight: 700, t0: -1e9 });
      NB.write('♪ ' + CREDIT, 190, 176, { font: 'ui', size: 26, color: PN.pencil, t0: t0 + 0.4, dur: 0.3 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('jg-map', '⌂', m.x, m.y, fx)) { this.back(); return; }
      if (S) {
        if (UI.iconButton('jg-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('jg-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, primary: true })) this.toggle();
      }
      if (t == null) return;
      // Finish: once ≥ 90% of the song has been heard (below the HUD, top right)
      this._finBox = null;
      if (this.canFinish() && !this.ended) {
        const w = UI.tap(250), h = UI.tap(84), fy = m.y + m.r + 30 + h / 2, fxp = m.x + m.r - w / 2;
        this._finBox = { x: fxp, y: fy, w, h };
        if (UI.button('jg-finish', 'Finish ✓', fxp, fy, { w, h, size: UI.text(40), primary: true })) { this.finish(); return; }
      }
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('jg-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 18 : 10, sy = 1080 - sh;
      g.save();
      g.fillStyle = PN.pencilLight; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = PN.ink; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = PN.redpen; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) NB.pencil(clamp(1920 * t / S.dur, 26, 1894), sy, touch ? 0.9 : 0.6);
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100);
        NB.write(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, { font: 'ui', size: 44, weight: 700, align: 'center' });
      }
      if (!this.startedOnce && !this.frozen) NB.write('loading the jingle…', 960, 590, { font: 'ui', size: 44, color: PN.pencil, align: 'center', t0: this.enteredAt + 0.3 });
      if (this.ended && Game.current() === this) {
        g.save(); g.fillStyle = 'rgba(252,252,248,0.6)'; g.fillRect(0, 380, 1920, 360); g.restore();
        if (UI.button('jg-again', '↺ Play it again', 700, 560, { w: 440, h: 110, size: 50, secondary: true })) this.restart();
        if (UI.button('jg-back', 'Back to the map', 1220, 560, { w: 440, h: 110, size: 50, primary: true })) this.back();
      }
    }
  }
  Jingle.STEP = STEP;
  Jingle.Song = Song;
  Jingle.plan = plan;
  Jingle.nums = nums;
  Jingle.flipsAt = flipsAt;
  Jingle.KEY = KEY;
  Levels.jingle = Jingle;
})();
